import { Outlet } from 'react-router-dom';

import { useAuth } from '@/pages/auth';

import { Topbar } from './Topbar';
import { DashboardSidebar } from './DashboardSidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { TopNav } from './TopNav';
import { Footer } from './Footer';
import { ImpersonationBanner } from './ImpersonationBanner';
import { SiteBanner } from './SiteBanner';

/**
 * Profiles are public, but render inside the dashboard chrome for authenticated
 * users (familiar navigation) and the public chrome for everyone else.
 */
export function ProfileLayout() {
  const authenticated = useAuth((s) => s.status === 'authenticated');

  if (authenticated) {
    return (
      <div className="min-h-screen bg-canvas">
        {/* Yellow banner above every layout chrome — see comment in
            DashboardLayout / DefaultLayout. Renders nothing when no admin
            impersonation session is active. */}
        <ImpersonationBanner />
        <SiteBanner />
        <Topbar />
        <div className="mx-auto flex max-w-7xl gap-6 px-4 max-[420px]:px-3 sm:px-6">
          <DashboardSidebar />
          {/* `pb-20` (80px) matches DashboardLayout so the MobileBottomNav
              (~56px content + env(safe-area-inset-bottom) on notched
              iPhones) doesn't overlap the page content. `pb-16` (64px)
              was under-clearing on safe-area devices by 8-12px.
              (H3 / M5 fix from the multi-agent test.) */}
          <main className="min-w-0 flex-1 py-6 pb-20 lg:pb-6">
            <Outlet />
          </main>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      {/* Impersonation banner also above the unauthenticated chrome —
          impossible in practice (an impersonation session is by definition
          authenticated) but cheap to include and keeps every layout
          consistent. */}
      <ImpersonationBanner />
      <SiteBanner />
      {/* Sky-blue brand bar (white links) for the public profile chrome —
          makes the unauthenticated /user/<username> header pop instead of
          blending into the white page surface. */}
      <TopNav variant="brand" />
      {/* Wrap the public-profile main in the SAME max-w-7xl + px-4/px-6
          gutter the authenticated branch uses (line 22 above). Without
          it, the logged-out profile rendered edge-to-edge while the
          logged-in version had ~24px side padding — visually
          inconsistent across the same page.
          I8: `pt-0` for the anon branch — the user explicitly asked
          for the cover to sit flush against the top brand bar
          (`mt-6 → mt-0` in Profile.tsx). The Profile.tsx change is
          a no-op while this main keeps `py-6` adding 24px back; drop
          the top padding here and keep bottom-6 for breathing room
          above the Footer. */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-6 max-[360px]:px-3 sm:mt-6 sm:px-6">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
